import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import TextInputField from 'components/TextInputField';
import MagicButton from 'components/MagicButton/MagicButton';
import CountrySelect from 'components/CountrySelect';
import EmailChangeModal from 'components/EmailChangeModal/EmailChangeModal';
import MobileVerificationModal from 'components/MobileVerificationModal/MobileVerificationModal';
import {
  useGetProfileQuery,
  useUpdateProfileMutation,
  useGetEmailChangeStatusQuery,
  useResendVerificationMutation,
  ProfileData,
} from 'store/services/auth/authApi';
import { useGetMeQuery } from 'store/services/user/meApi';
import { SettingsCard, SettingsRow, SettingsSection } from '../SettingsRow/SettingsRow';
import '../../Settings.css';
import './AccountTab.css';

interface AccountForm {
  username: string;
  firstName: string;
  lastName: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  email: string;
  phone: string;
}

const EMPTY_FORM: AccountForm = {
  username: '',
  firstName: '',
  lastName: '',
  address1: '',
  address2: '',
  city: '',
  state: '',
  zip: '',
  country: '',
  email: '',
  phone: '',
};

type Section = 'username' | 'address';

/** The profile payload as the form's flat shape. Handles the nested shippingAddress (new) and flat fields (old). */
const toForm = (profile: ProfileData): AccountForm => ({
  username: profile.username || '',
  firstName: profile.shippingAddress?.firstName || profile.shippingFirstName || '',
  lastName: profile.shippingAddress?.lastName || profile.shippingLastName || '',
  address1: profile.shippingAddress?.address1 || profile.shippingAddress1 || '',
  address2: profile.shippingAddress?.address2 || profile.shippingAddress2 || '',
  city: profile.shippingAddress?.city || profile.shippingCity || '',
  state: profile.shippingAddress?.state || profile.shippingState || '',
  zip: profile.shippingAddress?.postalCode || profile.shippingPostalCode || '',
  country: profile.shippingAddress?.country || profile.shippingCountry || '',
  email: profile.email || '',
  phone: profile.phoneNumber || profile.phone || '',
});

const ADDRESS_FIELDS: (keyof AccountForm)[] = ['firstName', 'lastName', 'address1', 'address2', 'city', 'state', 'zip', 'country'];

const VerifiedChip: React.FC = () => <span className="account-verified-chip">Verified</span>;

/**
 * Settings > Account: username, email, phone, shipping address. There is no
 * page-wide edit mode: each item shows its value with its own Edit or Change
 * button, edits inline in its own card, and saves on its own. Saving one item
 * never carries another item's unsaved edits (the request is built from the
 * last saved values plus the item being saved).
 */
const AccountTab: React.FC = () => {
  const [saved, setSaved] = useState<AccountForm>(EMPTY_FORM);
  const [draft, setDraft] = useState<AccountForm>(EMPTY_FORM);
  const [editing, setEditing] = useState<Section | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isPhoneModalOpen, setIsPhoneModalOpen] = useState(false);

  const { data: profileData, isLoading: isLoadingProfile, refetch: refetchProfile } = useGetProfileQuery();
  const [updateProfile, { isLoading: isSaving }] = useUpdateProfileMutation();
  const { data: emailChangeStatus, refetch: refetchEmailStatus } = useGetEmailChangeStatusQuery();
  const { data: userData } = useGetMeQuery();
  const [resendVerification] = useResendVerificationMutation();

  useEffect(() => {
    if (!profileData) return;
    const next = toForm(profileData);
    setSaved(next);
    setDraft(next);
  }, [profileData]);

  useEffect(() => {
    refetchEmailStatus();
  }, [profileData, refetchEmailStatus]);

  const change = (field: keyof AccountForm, value: string) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
    setSaveError(null);
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const startEditing = (section: Section) => {
    setDraft(saved);
    setErrors({});
    setSaveError(null);
    setEditing(section);
  };

  const cancel = () => {
    setDraft(saved);
    setErrors({});
    setSaveError(null);
    setEditing(null);
  };

  const validate = (section: Section): boolean => {
    const next: Record<string, string> = {};
    if (section === 'username') {
      if (!draft.username.trim()) next.username = 'Username is required';
    } else {
      if (!draft.firstName.trim()) next.firstName = 'First name is required';
      if (!draft.lastName.trim()) next.lastName = 'Last name is required';
      if (!draft.address1.trim()) next.address1 = 'Address line 1 is required';
      if (!draft.city.trim()) next.city = 'City is required';
      if (!draft.state.trim()) next.state = 'State/Province is required';
      if (!draft.zip.trim()) next.zip = 'ZIP/Postal code is required';
      if (!draft.country) next.country = 'Country is required';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const save = async (section: Section) => {
    if (!validate(section)) return;
    // The update endpoint takes the whole profile, so the fields NOT being edited
    // have to come from somewhere. They are re-read from the server right here,
    // not taken from what this tab loaded when it opened: a tab left open while
    // the address changed elsewhere must not write the old address back when
    // the username is saved.
    //
    // If the re-read fails, nothing is sent. Falling back to what the tab last
    // saw would be the very write this guards against, and one of those fields
    // is the address people mail cards to. (A failed refetch still carries the
    // previous `data`, so the error flag is what is checked.)
    const fresh = await refetchProfile();
    if (fresh.isError || !fresh.data) {
      toast.error('Could not load your current profile. Nothing was saved; please try again.');
      return;
    }
    const base = toForm(fresh.data);
    const next: AccountForm = { ...base };
    if (section === 'username') next.username = draft.username;
    else ADDRESS_FIELDS.forEach((field) => { next[field] = draft[field]; });

    try {
      await updateProfile({
        username: next.username,
        firstName: next.firstName,
        lastName: next.lastName,
        address1: next.address1,
        address2: next.address2,
        city: next.city,
        state: next.state,
        postalCode: next.zip,
        country: next.country,
        phone: next.phone,
      }).unwrap();
      setSaved(next);
      setDraft(next);
      setEditing(null);
      toast.success(section === 'username' ? 'Username updated' : 'Shipping address updated');
    } catch (error: any) {
      console.error('Failed to save profile:', error);
      setSaveError(error?.data?.message || 'Failed to save. Please try again.');
    }
  };

  const resend = async () => {
    try {
      if (!userData?.email) {
        toast.error('Unable to send verification email. Please try again.');
        return;
      }
      await resendVerification({ email: userData.email }).unwrap();
      toast.success('Verification email sent! Please check your inbox.');
    } catch {
      toast.error('Failed to send verification email. Please try again.');
    }
  };

  const hasAddress = !!(saved.address1 || saved.firstName);

  const actions = (section: Section) => (
    <div className="account-edit-actions">
      {saveError && <span className="validation-error">{saveError}</span>}
      <MagicButton label="Cancel" color="dark-grey" onPress={cancel} isDisabled={isSaving} />
      <MagicButton label={isSaving ? 'Saving...' : 'Save'} color="purple" onPress={() => save(section)} isDisabled={isSaving} />
    </div>
  );

  const field = (name: keyof AccountForm, placeholder: string) => (
    <div className="settings-field-container">
      <TextInputField placeholder={placeholder} value={draft[name]} onChange={(val) => change(name, val)} />
      {errors[name] && <div className="validation-error">{errors[name]}</div>}
    </div>
  );

  if (isLoadingProfile && !profileData) return null;

  return (
    <>
      <SettingsSection label="Username">
        <SettingsCard>
          <SettingsRow
            title={saved.username || 'Not set'}
            description="Your public name on TradeMagic."
            control={
              editing !== 'username' && (
                <button type="button" className="account-edit-btn" onClick={() => startEditing('username')}>
                  Edit
                </button>
              )
            }
          />
          {editing === 'username' && (
            <div className="account-edit-panel" data-testid="account-edit-username">
              {field('username', 'Choose a unique username')}
              {actions('username')}
            </div>
          )}
        </SettingsCard>
      </SettingsSection>

      <SettingsSection label="Email">
        <SettingsCard>
          <SettingsRow
            title={
              <>
                {saved.email || 'Not set'}
                {userData?.emailVerified && <VerifiedChip />}
              </>
            }
            description={
              <>
                {userData && !userData.emailVerified && (
                  <>
                    Email not verified.{' '}
                    <button type="button" className="settings-inline-link" onClick={resend}>
                      Resend verification
                    </button>
                  </>
                )}
                {emailChangeStatus?.pendingEmail && <> Pending change to: {emailChangeStatus.pendingEmail}</>}
              </>
            }
            control={
              <button type="button" className="account-edit-btn" onClick={() => setIsEmailModalOpen(true)}>
                Change
              </button>
            }
          />
        </SettingsCard>
      </SettingsSection>

      <SettingsSection label="Phone">
        <SettingsCard>
          <SettingsRow
            title={
              <>
                {userData?.phoneNumber || saved.phone || 'No phone number added'}
                {userData?.phoneVerified && <VerifiedChip />}
              </>
            }
            description={userData && !userData.phoneVerified ? 'Phone not verified.' : undefined}
            control={
              <button type="button" className="account-edit-btn" onClick={() => setIsPhoneModalOpen(true)}>
                {!userData?.phoneNumber && !saved.phone ? 'Add' : userData?.phoneVerified ? 'Change' : 'Verify now'}
              </button>
            }
          />
        </SettingsCard>
      </SettingsSection>

      <SettingsSection label="Shipping address">
        <SettingsCard>
          <SettingsRow
            title={hasAddress ? `${saved.firstName} ${saved.lastName}`.trim() || 'Shipping address' : 'No shipping address yet'}
            description={
              hasAddress ? (
                <span className="address-multiline">
                  {saved.address1 && <span className="account-address-line">{saved.address1}</span>}
                  {saved.address2 && <span className="account-address-line">{saved.address2}</span>}
                  {(saved.city || saved.state) && (
                    <span className="account-address-line">
                      {saved.city}, {saved.state} {saved.zip}
                    </span>
                  )}
                  {saved.country && <span className="account-address-line">{saved.country}</span>}
                </span>
              ) : (
                'Add one so traders can ship to you.'
              )
            }
            control={
              editing !== 'address' && (
                <button type="button" className="account-edit-btn" onClick={() => startEditing('address')}>
                  {hasAddress ? 'Edit' : 'Add'}
                </button>
              )
            }
          />
          {editing === 'address' && (
            <div className="account-edit-panel" data-testid="account-edit-address">
              <div className="settings-row">
                {field('firstName', 'First name')}
                {field('lastName', 'Last name')}
              </div>
              {field('address1', 'Address line 1')}
              {field('address2', 'Address line 2 (optional)')}
              <div className="settings-row">
                {field('city', 'City')}
                {field('state', 'State/Province')}
              </div>
              <div className="settings-row">
                {field('zip', 'ZIP/Postal code')}
                <div className="settings-field-container">
                  <CountrySelect value={draft.country} onChange={(val) => change('country', val)} error={errors.country} />
                </div>
              </div>
              {actions('address')}
            </div>
          )}
        </SettingsCard>
        <p className="settings-note">Traders see this only after a trade is confirmed, so they can ship to you.</p>
      </SettingsSection>

      <EmailChangeModal
        isOpen={isEmailModalOpen}
        onClose={() => {
          setIsEmailModalOpen(false);
          refetchEmailStatus();
        }}
        currentEmail={saved.email}
      />

      <MobileVerificationModal
        isOpen={isPhoneModalOpen}
        onClose={() => setIsPhoneModalOpen(false)}
        currentPhone={userData?.phoneNumber}
        onVerificationSuccess={() => {
          // Refresh user data to get updated phone verification status
          window.location.reload();
        }}
      />
    </>
  );
};

export default AccountTab;
